import {
  Button,
  CashIcon,
  ConfirmationModal,
  CurrencyInput,
  CustomToast,
  Input,
  MinusCircleIcon,
  Modal,
  Select,
} from '@/components';
import {getLedgerReasonOptions} from '@/constants/formatters';
import {LedgerReasons} from '@/types/Transaction';

import ConfirmationLabel from './ConfirmationLabel';
import type {FundBusinessWalletProps} from './types';
import useFundBusinessWallet, {
  WALLET_TYPE_LABELS,
} from './useFundBusinessWallet';

const initialResponse = {
  message: '',
  status: '',
};

const MODAL = 'business-details-balances-fund';

const FundBusinessWallet = ({
  business,
  walletType,
  initialAction,
  isOpen,
  onClose,
}: FundBusinessWalletProps) => {
  const {
    amount,
    currency,
    loading,
    showModal,
    response,
    action,
    handleAmountChange,
    handleCurrencyChange,
    handleLedgerButtonClick,
    isValidTransaction,
    handleLedger,
    setShowModal,
    setResponse,
    handleSelectReason,
    fundWalletReason,
    note,
    handleNoteChange,
  } = useFundBusinessWallet(
    business,
    walletType,
    initialAction,
    initialResponse,
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={`${initialAction === 'credit' ? 'Credit' : 'Debit'} ${WALLET_TYPE_LABELS[walletType]} Wallet`}
      width="w-96"
      dataCy={MODAL}
    >
      <div className="flex flex-col gap-6 mb-3">
        <CurrencyInput
          onAmountChange={handleAmountChange}
          onCurrencyChange={handleCurrencyChange}
          selectedCurrency={currency}
          amount={amount}
          name="amount"
        />

        <Select
          onChange={handleSelectReason}
          value={fundWalletReason}
          name="fundWalletReason"
          dataCy={`${MODAL}-reason`}
          options={getLedgerReasonOptions()}
          placeholder="Select a Reason"
          label="Attach Reason"
          bodyClassName="dark:bg-dark-inputBg"
        />

        {fundWalletReason === LedgerReasons.OTHERS && (
          <Input
            name="note"
            label="Note"
            placeholder="Type in the reason for payment"
            value={note}
            onChange={handleNoteChange}
            dataCy={`${MODAL}-note`}
            className="dark:bg-dark-inputBg w-full"
          />
        )}

        {/* The action (credit/debit) was already chosen on the wallet card
            that opened this modal — one confirm button matching it, not a
            second credit-vs-debit choice. */}
        <Button
          variant="outline"
          className="border-indigo-300"
          name={action}
          disabled={isValidTransaction()}
          onClick={() => handleLedgerButtonClick(action)}
          colorScheme={action === 'credit' ? 'green' : 'red'}
          dataCy={`${MODAL}-submit`}
          leftIcon={
            action === 'credit' ? (
              <CashIcon className="h-5 w-5" />
            ) : (
              <MinusCircleIcon className="h-5 w-5" />
            )
          }
        >
          {action === 'credit' ? 'Credit' : 'Debit'}
        </Button>
      </div>

      {response.status && (
        <CustomToast
          variant={response.status}
          message={response.message}
          onClose={() => setResponse(initialResponse)}
          dataCy={`${MODAL}-response`}
        />
      )}

      <ConfirmationModal
        isOpen={showModal}
        dataCy={`${MODAL}-confirm`}
        label={
          <ConfirmationLabel
            action={action}
            amount={amount}
            currency={currency}
            business={business}
            fundWalletReason={fundWalletReason}
            walletType={walletType}
          />
        }
        cancelAction={() => setShowModal(false)}
        cancelActionLabel="No"
        confirmAction={handleLedger}
        confirmActionLabel="Yes"
        loadingLabel={loading ? '' : ''}
        loading={loading}
        title={`Confirm ${action}`}
      />
    </Modal>
  );
};

export default FundBusinessWallet;
